// TimeSlotGrid — ActivityWizard Steg 3 (delspår multi-bana). Renders available start-time
// pills for the chosen date + duration, derived from hall opening/closing hours in time_step
// increments and filtered by client-side N-court availability (one bookings-query per date
// via useDayCourtBookings). Modes (wizardSlots.isSlotAvailableMulti): none = all open-hours
// slots, auto = at least N of the auto pool free SIMULTANEOUSLY, specific = all N chosen
// courts free. Past slots (today) hidden. Read-only preview — authoritative check is at 3g.
//
// Multi-bana core: requiredCourts = deriveRequiredCourts(spotsTotal). The grid shows ONLY
// times where N courts fit the whole player count — you can't pick a time unless everyone
// fits (operator's hard requirement). When the N/mode/court change invalidates the chosen
// time, onInvalidateTime() clears it (court-before-time pattern). Overlap math is epoch-based
// (inherited via courtHasOverlap), never string ISO compare.

import { useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  type CourtMode,
  type DayCourtBooking,
  generateStartTimes,
  hhmmToMinutes,
  isSlotAvailableMulti,
  minutesToHHMM,
} from "@/lib/wizardSlots";

interface HallTimes {
  opening_time: string;
  closing_time: string;
  time_step_minutes: number;
}

interface TimeSlotGridProps {
  date: string;
  durationMinutes: number;
  // Availability mode (Step3 derives mode = wantsCourt ? courtMode : 'none').
  mode: CourtMode;
  // Auto pool = courts with max_players >= 4 (2-player court excluded). Used in auto mode.
  autoPoolCourtIds: string[];
  // The N courts the user picked in specific mode (MultiCourtPicker).
  selectedCourtIds: string[];
  // N = deriveRequiredCourts(spotsTotal) — how many courts the activity needs.
  requiredCourts: number;
  hall: HallTimes | null;
  bookings: DayCourtBooking[];
  isLoading: boolean;
  startTime: string | null;
  onSelect: (start: string, end: string) => void;
  // Called when the currently-chosen startTime is no longer in the available set. Must be stable.
  onInvalidateTime: () => void;
}

export default function TimeSlotGrid({
  date,
  durationMinutes,
  mode,
  autoPoolCourtIds,
  selectedCourtIds,
  requiredCourts,
  hall,
  bookings,
  isLoading,
  startTime,
  onSelect,
  onInvalidateTime,
}: TimeSlotGridProps) {
  const { t } = useTranslation("admin");

  const freeSlots = useMemo(() => {
    if (!hall || !date || !durationMinutes) return [];
    const openingMin = hhmmToMinutes(hall.opening_time);
    const closingMin = hhmmToMinutes(hall.closing_time);
    const starts = generateStartTimes(openingMin, closingMin, hall.time_step_minutes, durationMinutes);
    const nowMs = Date.now();
    const result: Array<{ start: string; end: string }> = [];
    for (const s of starts) {
      const startDate = new Date(`${date}T${s}:00`);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      if (startDate.getTime() <= nowMs) continue; // hide past slots (today)
      const free = isSlotAvailableMulti(
        startDate.toISOString(),
        endDate.toISOString(),
        mode,
        autoPoolCourtIds,
        selectedCourtIds,
        bookings,
        requiredCourts,
      );
      if (free) {
        result.push({ start: s, end: minutesToHHMM(hhmmToMinutes(s) + durationMinutes) });
      }
    }
    return result;
  }, [hall, date, durationMinutes, mode, autoPoolCourtIds, selectedCourtIds, requiredCourts, bookings]);

  // Close the availability gap: if the chosen time is no longer available for the current
  // N/mode/court, clear it so the user re-picks. Guarded on !isLoading (bookings still
  // loading → freeSlots transiently empty) and on startTime being set (never fires on mount).
  useEffect(() => {
    if (isLoading || !startTime) return;
    if (!freeSlots.some((slot) => slot.start === startTime)) {
      onInvalidateTime();
    }
  }, [freeSlots, startTime, isLoading, onInvalidateTime]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-xl border border-border bg-muted/40" />
        ))}
      </div>
    );
  }

  if (freeSlots.length === 0) {
    // N-court-aware empty state: when a court is wanted, explain that no time fits N courts
    // (not the generic "no times"). N inlined so the user understands the constraint.
    const emptyKey = mode === "none" ? "activityWizard.step2.noSlotsAvailable" : "activityWizard.step3.noNCourtsAvailable";
    const emptyDefault =
      mode === "none"
        ? "Inga lediga tider"
        : `Inga tider med ${requiredCourts} lediga banor den dagen`;
    return (
      <p
        data-testid="no-slots"
        className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground"
      >
        {t(emptyKey, { n: requiredCourts, defaultValue: emptyDefault })}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {freeSlots.map((slot) => (
        <button
          key={slot.start}
          type="button"
          aria-pressed={startTime === slot.start}
          data-testid={`slot-${slot.start}`}
          onClick={() => onSelect(slot.start, slot.end)}
          className={cn(
            "rounded-xl border p-2.5 text-sm font-medium tabular-nums transition-colors",
            startTime === slot.start
              ? "livfull-pill-selected"
              : "border-border hover:border-primary/50",
          )}
        >
          {slot.start}
        </button>
      ))}
    </div>
  );
}
